import { useNavigate, useParams } from 'react-router-dom'
import { useLazyQuery, useMutation } from '@apollo/client'
import { useCallback, useEffect, useState } from 'react'
import { useRecoilValue } from 'recoil'
import { Form, UploadFile } from 'antd'
import _ from 'lodash'

import { UpdateActiveHealthPartnerInput } from '@/gql/activeHealthPartner/backofficeUpdateActiveHealthPartner/interface'
import { usePartnerLogoUpload } from '@/services/wellBeing/usePartnerLogoUpload'
import { activeHealthPartnerGql } from '@/gql'

import ConfirmModalHook from '@/components/common/ConfirmModal/ConfirmModal.hook'
import { usePartnerCanDelete } from '@/services/wellBeing/usePartnerCanDelete'
import { BACKOFFICE_USER_PERMISSION } from '@/constant/BACKOFFICE_PERMISSION'
import { validatePerm } from '@/libs/validatePermission'
import userAtom from '@/recoil/user'
import message from '@/libs/message'
import CONSTANT from '@/constant'

const WellBeingPartnerPageHook = () => {
  const navigate = useNavigate()
  const user = useRecoilValue(userAtom)
  const isRemovePartner = validatePerm([BACKOFFICE_USER_PERMISSION.WBE010106], user.permissions)

  const [form] = Form.useForm()
  const { partnerId } = useParams<{ partnerId: string }>()
  const [isButtonDisabled, setIsButtonDisabled] = useState(true)
  const [logoPartnerFiles, setLogoPartnerFiles] = useState<UploadFile[]>([])
  const [isLoading, setLoading] = useState<boolean>(false)
  const { setConfirmModal } = ConfirmModalHook()

  // Fetch partner data
  const [
    backofficeGetActiveHealthPartnerById,
    { data: partnerData, loading: partnerLoading, refetch },
  ] = useLazyQuery(activeHealthPartnerGql.query.backofficeGetActiveHealthPartnerById, {
    fetchPolicy: 'no-cache',
  })
  const getActiveHealthPartnerById = useCallback(
    (id: string) => {
      backofficeGetActiveHealthPartnerById({
        variables: {
          input: {
            id: id,
            relations: ['activeHealthPartnerCategory'],
          },
        },
      })
    },
    [backofficeGetActiveHealthPartnerById],
  )

  // Response partner data
  const getPartnerData = () => ({
    displayNameTh: partnerData?.backofficeGetActiveHealthPartnerById.displayNameTh,
    displayNameEn: partnerData?.backofficeGetActiveHealthPartnerById.displayNameEn,
    categoryId: partnerData?.backofficeGetActiveHealthPartnerById.activeHealthPartnerCategory.id,
    isActive: partnerData?.backofficeGetActiveHealthPartnerById.isActive,
  })

  // Handle button save
  const handleFormComplete = () => {
    const values = form.getFieldsValue()
    const isEmpty = Object.values(values || []).some((item: any) => item == null)
    const isSame = _.isEqual(values, getPartnerData())
    const isLogoUpdated = logoPartnerFiles?.length > 0
    setIsButtonDisabled((isEmpty || isSame) && !isLogoUpdated)
  }

  // Use GraphQL mutation for updating partner data
  const [mutationUpdatePartner, { loading: isMutationUpdateLoading }] = useMutation(
    activeHealthPartnerGql.mutation.backofficeUpdateActiveHealthPartner,
    {
      fetchPolicy: 'no-cache',
    },
  )

  // Function to update partner details
  const updatePartner = async (id: string, formValues: any) => {
    try {
      const { displayNameTh, displayNameEn, categoryId, isActive } = formValues
      const input: UpdateActiveHealthPartnerInput = {
        id,
        displayNameTh,
        displayNameEn,
        categoryId,
        isActive,
      }

      await mutationUpdatePartner({ variables: { input } })
      message.success({ content: 'แก้ไขพาร์ทเนอร์สำเร็จ', duration: 10 })
    } catch (error: any) {
      message.error(error?.message || 'กรุณาตรวจสอบข้อมูลและลองใหม่อีกครั้ง')
    }
  }

  // Update partner logo
  const { mutateAsync: uploadLogo } = usePartnerLogoUpload(partnerId)

  // Handle update partner
  const handleUpdatePartner = async (): Promise<void> => {
    try {
      setLoading(true)
      const values = await form.validateFields()

      if (!partnerId) return

      if (logoPartnerFiles.length > 0) {
        // Update file status to "uploading"
        setLogoPartnerFiles(
          logoPartnerFiles.map((upload) => ({ ...upload, percent: 60, status: 'uploading' })),
        )

        // Upload the first file
        await uploadLogo(logoPartnerFiles[0].originFileObj as File)

        // Message update logo
        message.success({ content: 'แก้ไขโลโก้พาร์ทเนอร์สำเร็จ', duration: 10 })
      }

      if (!_.isEqual(values, getPartnerData())) {
        await updatePartner(partnerId, values)
      }

      refetch()
    } catch (error: any) {
      message.error({ content: error?.response?.data?.message || 'กรุณาตรวจสอบข้อมูลให้ถูกต้อง' })
    } finally {
      // Clear files after successful upload
      setLogoPartnerFiles([])
      setLoading(false)
    }
  }

  // Form submit handler
  const onSubmit = async (): Promise<void> => {
    setConfirmModal({
      onOkFunction: () => {
        setConfirmModal({
          isVisible: false,
          onOkFunction: Function,
          dataTestId: 'update-partner-information-modal-action',
        })
        handleUpdatePartner()
      },
      isVisible: true,
      title: 'คุณได้มีการแก้ไขข้อมูล',
      bodyText: 'ต้องการบันทึกการแก้ไขทั้งหมดหรือไม่',
      okText: 'บันทึก',
      cancelText: 'ไม่ต้องบันทึก',
      dataTestId: 'update-partner-information-modal',
    })
  }

  // Use GraphQL mutation for removing partner
  const [mutationRemovePartner] = useMutation(
    activeHealthPartnerGql.mutation.backofficeRemoveActiveHealthPartner,
    {
      fetchPolicy: 'no-cache',
    },
  )

  // Handle remove partner
  const handleRemovePartner = async (): Promise<void> => {
    if (!partnerId) return

    setLoading(true)

    try {
      await mutationRemovePartner({ variables: { activeHealthPartnerId: partnerId } })
      message.success({ content: 'ลบพาร์ทเนอร์สำเร็จ', duration: 10 })
      navigate(CONSTANT.ROUTES.WELL_BEING_PAGE)
    } catch (error: any) {
      message.error({ content: error?.response?.data?.message || 'กรุณาตรวจสอบข้อมูลให้ถูกต้อง' })
    } finally {
      setLoading(false)
    }
  }

  // Fetch partner can delete
  const fetchPartnerCanDelete = usePartnerCanDelete(partnerId)

  // Modal confirm delete
  const showDeleteConfirmationModal = () => {
    const partnerName = `“${partnerData?.backofficeGetActiveHealthPartnerById?.displayNameTh} (${partnerData?.backofficeGetActiveHealthPartnerById?.displayNameEn})”`

    setConfirmModal({
      isVisible: true,
      title: 'คุณแน่ใจที่จะลบพาร์ทเนอร์หรือไม่',
      bodyText: `เมื่อลบพาร์ทเนอร์ ${partnerName} พาร์ทเนอร์และสาขาภายใต้พาร์ทเนอร์นี้จะ ถูกลบไปทั้งหมด`,
      okText: 'ลบ',
      cancelText: 'ยกเลิก',
      dataTestId: 'remove-partner-information-modal',
      okButtonProps: { type: 'primary', danger: true },
      onOkFunction: () => {
        handleRemovePartner()
        setConfirmModal({
          isVisible: false,
          onOkFunction: Function,
        })
      },
    })
  }

  // Modal partner cannot delete
  const showCannotDeleteModal = () => {
    setConfirmModal({
      isVisible: true,
      title: 'ไม่สามารถลบพาร์ทเนอร์ได้',
      bodyText: 'เนื่องจากมีสาขาที่มีการใช้งานสแกนรับ Well Point แล้ว',
      okText: 'รับทราบ',
      cancelText: undefined,
      cancelButtonProps: { style: { display: 'none' } },
      dataTestId: 'remove-partner-information-modal',
      onOkFunction: () => {
        setConfirmModal({
          isVisible: false,
          onOkFunction: Function,
        })
      },
    })
  }

  // Button remove partner
  const onClickRemove = async (): Promise<void> => {
    const result = await fetchPartnerCanDelete.refetch()

    if (!result.isSuccess) return

    result.data.canDelete ? showDeleteConfirmationModal() : showCannotDeleteModal()
  }

  useEffect(() => {
    if (!partnerId) return
    handleFormComplete()
  }, [logoPartnerFiles, partnerId])

  useEffect(() => {
    if (!partnerId) return
    getActiveHealthPartnerById(partnerId)
  }, [getActiveHealthPartnerById, partnerId])

  useEffect(() => {
    if (!partnerData) return
    form.setFieldsValue(getPartnerData())
    handleFormComplete()
  }, [partnerData])

  return {
    form,
    partnerId,
    partnerData,
    logoPartnerFiles,
    isButtonDisabled,
    isRemovePartner,
    isMutationUpdateLoading,
    partnerLoading,
    isLoading,
    handleFormComplete,
    setLogoPartnerFiles,
    onClickRemove,
    onSubmit,
  }
}

export default WellBeingPartnerPageHook
